import React, { useEffect, useMemo, useState } from "react";
import api, { dateDK, formatErr } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Wrench, Plus, Loader2, Search, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Mine opgaver — samlet overblik på tværs af alle biler.
 *
 * Sektioner:
 *  1. Værksted — biler markeret med needs_workshop (åbn ét klik = clear-workshop)
 *  2. Åbne opgaver pr. bil — grupperet, hver opgave har checkbox til hurtig afhakning
 *
 * Designet til at give Mahasa hurtigt overblik over hvad der reelt skal gøres.
 */
export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState({}); // {car_id: string draft}
  const [savingTask, setSavingTask] = useState({}); // {task_id: bool}
  const [clearingWorkshop, setClearingWorkshop] = useState({}); // {car_id: bool}
  const [statusFilter, setStatusFilter] = useState("active"); // active | lager | all
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get("/tasks", { params: { status_filter: "open" } }),
        api.get("/cars", { params: { include_revenue: false } }),
      ]);
      setTasks(t.data);
      setCars(c.data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const carById = useMemo(() => {
    const m = {};
    cars.forEach((c) => { m[c.id] = c; });
    return m;
  }, [cars]);

  const matchesStatus = (status) => {
    if (statusFilter === "all") return status !== "sold";
    if (statusFilter === "active") return status === "active";
    if (statusFilter === "lager") return status === "lager";
    return true;
  };

  const workshopCars = useMemo(() => {
    return cars
      .filter((c) => c.needs_workshop && matchesStatus(c.status))
      .sort((a, b) => (a.license_plate || "").localeCompare(b.license_plate || ""));
  }, [cars, statusFilter]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return tasks.filter((t) => {
      const car = carById[t.car_id];
      // Filter på status (skip biler vi ikke kender)
      if (!car || !matchesStatus(car.status)) return false;
      if (!s) return true;
      const hay = [
        t.description,
        t.license_plate,
        car?.make, car?.model,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [tasks, search, carById, statusFilter]);

  // Gruppér opgaver pr. bil
  const grouped = useMemo(() => {
    const byCar = {};
    filtered.forEach((t) => {
      if (!byCar[t.car_id]) byCar[t.car_id] = [];
      byCar[t.car_id].push(t);
    });
    // Sortér biler efter nummerplade
    const carIds = Object.keys(byCar).sort((a, b) => {
      const pa = byCar[a][0]?.license_plate || "";
      const pb = byCar[b][0]?.license_plate || "";
      return pa.localeCompare(pb);
    });
    return carIds.map((id) => ({ car_id: id, tasks: byCar[id] }));
  }, [filtered]);

  const completeTask = async (task) => {
    setSavingTask((s) => ({ ...s, [task.id]: true }));
    // Optimistisk fjern fra UI
    setTasks((arr) => arr.filter((x) => x.id !== task.id));
    try {
      await api.patch(`/tasks/${task.id}`, { status: "done" });
      toast.success(`Afkrydset: ${task.description.slice(0, 60)}`);
    } catch (e) {
      toast.error(formatErr(e));
      // Rul tilbage
      setTasks((arr) => [task, ...arr]);
    } finally {
      setSavingTask((s) => { const c = { ...s }; delete c[task.id]; return c; });
    }
  };

  const toggleHidden = async (task) => {
    const newVal = !task.hidden_from_dashboard;
    // Optimistic update
    setTasks((arr) => arr.map((x) => x.id === task.id ? { ...x, hidden_from_dashboard: newVal } : x));
    try {
      await api.patch(`/tasks/${task.id}`, { hidden_from_dashboard: newVal });
      toast.success(newVal ? "Skjult fra Overblik" : "Vises igen på Overblik");
    } catch (e) {
      toast.error(formatErr(e));
      // Roll back
      setTasks((arr) => arr.map((x) => x.id === task.id ? { ...x, hidden_from_dashboard: !newVal } : x));
    }
  };

  const clearWorkshop = async (car) => {
    setClearingWorkshop((s) => ({ ...s, [car.id]: true }));
    try {
      await api.post(`/cars/${car.id}/clear-workshop`);
      setCars((arr) => arr.map((c) => c.id === car.id ? { ...c, needs_workshop: false, workshop_reason: "" } : c));
      toast.success(`${car.license_plate}: Værksted-status fjernet`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setClearingWorkshop((s) => { const c = { ...s }; delete c[car.id]; return c; });
    }
  };

  const addTask = async (carId) => {
    const desc = (adding[carId] || "").trim();
    if (!desc) return;
    try {
      const r = await api.post("/tasks", { car_id: carId, description: desc });
      setTasks((arr) => [r.data, ...arr]);
      setAdding((s) => ({ ...s, [carId]: "" }));
      toast.success("Opgave tilføjet");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  // For "quick add" til biler uden eksisterende opgaver — alle aktive biler vises som tilvalg
  const carsWithoutTasks = useMemo(() => {
    const idsWithTasks = new Set(grouped.map((g) => g.car_id));
    return cars
      .filter((c) => matchesStatus(c.status) && !idsWithTasks.has(c.id))
      .sort((a, b) => (a.license_plate || "").localeCompare(b.license_plate || ""));
  }, [cars, grouped, statusFilter]);

  const totalOpen = filtered.length;
  const totalWorkshop = workshopCars.length;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1100px]" data-testid="tasks-page">
      <div className="mb-6 sm:mb-10 flex items-start justify-between gap-3">
        <div>
          <div className="overline text-zinc-400">Drift</div>
          <h1 className="headline text-3xl sm:text-4xl lg:text-5xl mt-2">Mine opgaver.</h1>
          <p className="text-zinc-500 text-xs sm:text-sm mt-1">
            Hurtigt overblik over hvad du skal handle på — værksted, åbne opgaver og hurtig afkrydsning.
          </p>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          className="rounded-none bg-[#C8A661] hover:bg-[#D4B574] text-black font-semibold whitespace-nowrap"
          data-testid="open-add-task"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Tilføj opgave
        </Button>
      </div>

      {/* Filter-toggle */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <FilterTab value="active" cur={statusFilter} onClick={setStatusFilter}>Aktive biler</FilterTab>
        <FilterTab value="lager" cur={statusFilter} onClick={setStatusFilter}>Lagerbiler</FilterTab>
        <FilterTab value="all" cur={statusFilter} onClick={setStatusFilter}>Begge</FilterTab>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søg plade, mærke, model, beskrivelse…"
            className="pl-9 rounded-none border-zinc-700"
            data-testid="tasks-search"
          />
        </div>
        <div className="flex gap-2 text-xs text-zinc-500" data-testid="tasks-counts">
          <Pill label="Værksted" value={totalWorkshop} color={totalWorkshop > 0 ? "red" : "zinc"} />
          <Pill label="Åbne opgaver" value={totalOpen} color={totalOpen > 0 ? "amber" : "zinc"} />
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Indlæser…</div>
      ) : (
        <div className="space-y-8">
          {/* Værksted-sektion */}
          {workshopCars.length > 0 && (
            <section data-testid="workshop-section">
              <h2 className="font-display tracking-tighter text-xl text-red-400 mb-3 flex items-center gap-2">
                <Wrench className="w-5 h-5" /> Skal på værksted
                <span className="text-zinc-500 text-xs font-sans tracking-normal">{workshopCars.length}</span>
              </h2>
              <div className="space-y-2">
                {workshopCars.map((c) => (
                  <div
                    key={c.id}
                    className="section-card p-3 sm:p-4 flex items-start gap-3 border-l-2 border-l-red-600"
                    data-testid={`workshop-row-${c.license_plate}`}
                  >
                    <button
                      onClick={() => clearWorkshop(c)}
                      disabled={clearingWorkshop[c.id]}
                      className="mt-0.5 w-6 h-6 border-2 border-red-500 hover:border-emerald-500 hover:bg-emerald-500/10 transition-colors flex items-center justify-center shrink-0 disabled:opacity-50"
                      title="Marker som klaret"
                      data-testid={`workshop-clear-${c.license_plate}`}
                    >
                      {clearingWorkshop[c.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="font-display font-bold text-lg tracking-tight text-white">{c.license_plate}</span>
                        <span className="text-zinc-500 text-xs">{[c.make, c.model].filter(Boolean).join(" ")}</span>
                        {c.status === "lager" && (
                          <span className="text-[10px] uppercase tracking-wider text-zinc-500 border border-zinc-700 px-1.5 py-0.5">Lager</span>
                        )}
                      </div>
                      <div className="text-sm text-red-300 mt-1 flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        {c.workshop_reason || <em className="text-zinc-500">Ingen årsag angivet</em>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Åbne opgaver pr. bil */}
          <section data-testid="tasks-section">
            <h2 className="font-display tracking-tighter text-xl text-[#C8A661] mb-3">
              Åbne opgaver
              <span className="ml-2 text-zinc-500 text-xs font-sans tracking-normal">{totalOpen}</span>
            </h2>
            {grouped.length === 0 ? (
              <div className="section-card p-6 text-center text-zinc-500 text-sm">
                Ingen åbne opgaver — alt er afhakket.
              </div>
            ) : (
              <div className="space-y-3">
                {grouped.map(({ car_id, tasks: list }) => {
                  const car = carById[car_id];
                  return (
                    <CarTaskGroup
                      key={car_id}
                      car={car}
                      tasks={list}
                      savingTask={savingTask}
                      onComplete={completeTask}
                      onToggleHidden={toggleHidden}
                      onAdd={addTask}
                      draft={adding[car_id] || ""}
                      setDraft={(v) => setAdding((s) => ({ ...s, [car_id]: v }))}
                    />
                  );
                })}
              </div>
            )}
          </section>

          {/* Hurtig opret-opgave for biler uden åbne opgaver */}
          {carsWithoutTasks.length > 0 && (
            <section>
              <h2 className="font-display tracking-tighter text-base text-zinc-400 mb-3">
                Tilføj opgave til bil uden åbne opgaver
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {carsWithoutTasks.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => {
                      const desc = window.prompt(`Ny opgave til ${c.license_plate}:`);
                      if (desc && desc.trim()) {
                        setAdding((s) => ({ ...s, [c.id]: desc.trim() }));
                        api.post("/tasks", { car_id: c.id, description: desc.trim() })
                          .then((r) => {
                            setTasks((arr) => [r.data, ...arr]);
                            setAdding((s) => ({ ...s, [c.id]: "" }));
                            toast.success("Opgave tilføjet");
                          })
                          .catch((e) => toast.error(formatErr(e)));
                      }
                    }}
                    className="border border-zinc-800 hover:border-[#C8A661] p-2 text-left transition-colors group"
                    data-testid={`quick-add-${c.license_plate}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-zinc-600 group-hover:text-[#C8A661]">Tilføj</div>
                    <div className="font-display font-bold text-sm text-zinc-300 group-hover:text-white">{c.license_plate}</div>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <AddTaskDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        cars={cars.filter((c) => c.status !== "sold")}
        onAdded={(t) => { setTasks((arr) => [t, ...arr]); }}
      />
    </div>
  );
}

function FilterTab({ value, cur, onClick, children }) {
  const active = cur === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={
        "px-3 py-1.5 text-xs uppercase tracking-wider transition-colors " +
        (active ? "bg-[#C8A661] text-black font-semibold" : "bg-zinc-800 text-zinc-400 hover:text-white")
      }
      data-testid={`filter-${value}`}
    >
      {children}
    </button>
  );
}

function AddTaskDialog({ open, onOpenChange, cars, onAdded }) {
  const [search, setSearch] = useState("");
  const [selectedCar, setSelectedCar] = useState(null);
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setSelectedCar(null);
      setDescription("");
    }
  }, [open]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    const arr = cars.slice().sort((a, b) => (a.license_plate || "").localeCompare(b.license_plate || ""));
    if (!s) return arr;
    return arr.filter((c) =>
      `${c.license_plate} ${c.make || ""} ${c.model || ""}`.toLowerCase().includes(s)
    );
  }, [cars, search]);

  const submit = async () => {
    if (!selectedCar || !description.trim()) return;
    setSaving(true);
    try {
      const r = await api.post("/tasks", { car_id: selectedCar.id, description: description.trim() });
      onAdded(r.data);
      toast.success(`Opgave tilføjet til ${selectedCar.license_plate}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-task-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tighter text-2xl">Tilføj opgave</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="overline text-[10px]">Vælg bil</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Søg plade, mærke, model…"
                className="pl-9 rounded-none border-zinc-700"
                data-testid="add-task-search"
              />
            </div>
            <div className="mt-2 max-h-56 overflow-y-auto border border-zinc-800">
              {filtered.length === 0 && (
                <div className="p-3 text-zinc-500 text-xs text-center">Ingen biler matcher</div>
              )}
              {filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedCar(c)}
                  className={
                    "w-full text-left px-3 py-2 text-sm border-b border-zinc-900 transition-colors flex items-center justify-between " +
                    (selectedCar?.id === c.id ? "bg-[#C8A661]/20 text-white" : "hover:bg-[#262626]")
                  }
                  data-testid={`select-car-task-${c.license_plate}`}
                >
                  <div>
                    <span className="font-display font-bold">{c.license_plate}</span>
                    <span className="text-zinc-500 ml-2 text-xs">{c.make} {c.model}</span>
                  </div>
                  <span className={"text-[9px] uppercase tracking-wider px-1.5 py-0.5 border " + (c.status === "lager" ? "border-zinc-700 text-zinc-500" : "border-emerald-700/40 text-emerald-400")}>
                    {c.status === "lager" ? "Lager" : "Aktiv"}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="overline text-[10px]">Beskrivelse</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && selectedCar && description.trim()) { e.preventDefault(); submit(); } }}
              placeholder="Fx 'Skift dæk'"
              className="rounded-none border-zinc-700 mt-1"
              data-testid="add-task-description"
              autoFocus={!!selectedCar}
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-none border-zinc-700">Annullér</Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!selectedCar || !description.trim() || saving}
            className="rounded-none bg-[#C8A661] hover:bg-[#D4B574] text-black font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="add-task-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Opret opgave
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CarTaskGroup({ car, tasks, savingTask, onComplete, onToggleHidden, onAdd, draft, setDraft }) {
  if (!car) return null;
  return (
    <div className="section-card p-3 sm:p-4" data-testid={`task-group-${car.license_plate}`}>
      <div className="flex flex-wrap items-baseline gap-2 mb-3 pb-2 border-b border-zinc-800">
        <span className="font-display font-bold text-lg tracking-tight text-white">{car.license_plate}</span>
        <span className="text-zinc-500 text-xs">{[car.make, car.model].filter(Boolean).join(" ")}</span>
        {car.status === "lager" && (
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 border border-zinc-700 px-1.5 py-0.5">Lager</span>
        )}
        {car.needs_workshop && (
          <span className="text-[10px] uppercase tracking-wider text-red-400 border border-red-700 px-1.5 py-0.5 flex items-center gap-1">
            <Wrench className="w-2.5 h-2.5" /> værksted
          </span>
        )}
        <span className="ml-auto text-xs text-zinc-500">{tasks.length} åben{tasks.length === 1 ? "" : "ne"}</span>
      </div>

      <div className="space-y-1.5 mb-3">
        {tasks.map((t) => {
          const hidden = !!t.hidden_from_dashboard;
          return (
            <div key={t.id} className="flex items-start gap-3 py-2 px-2 hover:bg-[#262626] transition-colors group" data-testid={`task-row-${t.id}`}>
              <button
                onClick={() => onComplete(t)}
                disabled={!!savingTask[t.id]}
                className="mt-0.5 w-5 h-5 border-2 border-zinc-600 hover:border-emerald-500 hover:bg-emerald-500/10 transition-colors flex items-center justify-center shrink-0 disabled:opacity-50"
                title="Marker udført"
                data-testid={`complete-task-${t.id}`}
              >
                {savingTask[t.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              </button>
              <button
                type="button"
                onClick={() => onToggleHidden(t)}
                className="flex-1 text-left text-sm text-zinc-200 group-hover:text-white cursor-pointer"
                title={hidden ? "Klik for at vise denne opgave på Overblik igen" : "Klik for at skjule denne opgave fra Overblik (stadig synlig her)"}
                data-testid={`task-toggle-hidden-${t.id}`}
              >
                {t.description}
                {hidden && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500 border border-zinc-700 px-1.5 py-0.5 align-middle">
                    <EyeOff className="w-2.5 h-2.5" /> Skjult fra overblik
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => onToggleHidden(t)}
                className={"shrink-0 mt-0.5 p-1 transition-colors " + (hidden ? "text-zinc-500 hover:text-[#C8A661]" : "text-[#C8A661] hover:text-zinc-500")}
                title={hidden ? "Vis på Overblik igen" : "Skjul fra Overblik"}
                data-testid={`task-eye-${t.id}`}
              >
                {hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
              <span className="text-[10px] text-zinc-600 shrink-0 mt-0.5">{dateDK(t.created_at)}</span>
            </div>
          );
        })}
      </div>

      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(car.id); } }}
          placeholder="Ny opgave (fx 'tjek bremser')"
          className="rounded-none border-zinc-700 h-8 text-xs flex-1"
          data-testid={`add-task-input-${car.license_plate}`}
        />
        <Button
          type="button"
          onClick={() => onAdd(car.id)}
          disabled={!draft.trim()}
          className="rounded-none bg-[#C8A661] hover:bg-[#D4B574] text-black h-8 px-3"
          data-testid={`add-task-btn-${car.license_plate}`}
        >
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function Pill({ label, value, color = "zinc" }) {
  const cls =
    color === "red" ? "border-red-700 text-red-400" :
    color === "amber" ? "border-[#C8A661]/40 text-[#C8A661]" :
    "border-zinc-800 text-zinc-400";
  return (
    <div className={"px-2.5 py-1 border " + cls}>
      <span className="uppercase tracking-wider text-[10px]">{label}</span>{" "}
      <span className="font-mono font-bold ml-1">{value}</span>
    </div>
  );
}
